import React, { useEffect, useState, Component } from 'react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
import { Check, X, Shield, Users2, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxjss3TgDQN6ihmsA6XV324S4E1Qh-L3Gn0sa5wCh8JWmOo15dZjogIMINaX5rVwLsN/exec';
interface TariffItem {
  text: string;
  included: boolean;
}
interface TariffSection {
  title: string;
  items: TariffItem[];
}
interface Tier {
  name: string;
  price: string;
  sections: TariffSection[];
  cta: string;
  ctaSecondary?: string;
  popular: boolean;
  isLeader?: boolean;
  href: string;
}
// Animated gradient text component
function AnimatedGradientText({
  children,
  isLeader = false,
  className = ''
}: {
  children: React.ReactNode;
  isLeader?: boolean;
  className?: string;
}) {
  const gradientClass = isLeader ? 'bg-gradient-to-r from-purple-600 via-purple-400 to-purple-600' : 'bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600';
  return <span className={`bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x ${gradientClass} ${className}`} data-id="element-541">
      {children}
    </span>;
}
// Consultation Modal Component
function ConsultationModal({
  isOpen,
  onClose,
  scrollPosition
}: {
  isOpen: boolean;
  onClose: () => void;
  scrollPosition: number;
}) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  const [formErrors, setFormErrors] = useState({
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollPosition}px`;
      setSubmitStatus('idle');
      setFormErrors({
        phone: '',
        email: ''
      });
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      // Restore scroll position
      window.scrollTo(0, scrollPosition);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen, scrollPosition]);
  const validateForm = (): boolean => {
    const errors = {
      phone: '',
      email: ''
    };
    let isValid = true;
    // Phone validation: must be 10, 12, or 13 characters (digits only)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10 && phoneDigits.length !== 12 && phoneDigits.length !== 13) {
      errors.phone = 'Номер телефону має містити 10, 12 або 13 цифр';
      isValid = false;
    }
    // Email validation: must contain @ if provided
    if (formData.email && !formData.email.includes('@')) {
      errors.email = 'Email має містити символ @';
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      // Send data to Google Sheets via Apps Script
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          source: 'Tariffs - Спеціальні умови',
          timestamp: new Date().toISOString()
        })
      });
      setSubmitStatus('success');
      setTimeout(() => {
        onClose();
        setFormData({
          name: '',
          phone: '',
          email: '',
          company: ''
        });
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-542">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-543">
        <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.9,
        opacity: 0
      }} className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#0A0F2A] to-[#1E2A5E] rounded-2xl border border-white/10 shadow-2xl p-6 md:p-8" onClick={e => e.stopPropagation()} data-id="element-544">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white" data-id="element-545">
            <X className="w-5 h-5" data-id="element-546" />
          </button>

          <h3 className="text-xl md:text-2xl font-bold text-white mb-2" data-id="element-547">
            Отримати консультацію
          </h3>
          <p className="text-gray-400 mb-6 text-sm md:text-base" data-id="element-548">
            Залиште свої дані і ми зв'яжемося з вами
          </p>

          {submitStatus === 'success' ? <div className="text-center py-8" data-id="element-549">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-550">
                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-551">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-552" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2" data-id="element-553">Дякуємо!</h4>
              <p className="text-gray-400" data-id="element-554">
                Ми зв'яжемося з вами найближчим часом
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-555">
              <div data-id="element-556">
                <label className="block text-sm text-gray-400 mb-2" data-id="element-557">
                  Ім'я та прізвище *
                </label>
                <input type="text" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors text-base" placeholder="Введіть ваше ім'я" required disabled={isSubmitting} data-id="element-558" />
              </div>

              <div data-id="element-559">
                <label className="block text-sm text-gray-400 mb-2" data-id="element-560">
                  Номер телефону *
                </label>
                <input type="tel" value={formData.phone} onChange={e => {
              setFormData({
                ...formData,
                phone: e.target.value
              });
              if (formErrors.phone) {
                setFormErrors({
                  ...formErrors,
                  phone: ''
                });
              }
            }} className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-gray-500 focus:outline-none transition-colors text-base ${formErrors.phone ? 'border-red-500' : 'border-white/20 focus:border-blue-500'}`} placeholder="+380" required disabled={isSubmitting} data-id="element-561" />
                {formErrors.phone && <p className="text-red-400 text-sm mt-1" data-id="element-562">
                    {formErrors.phone}
                  </p>}
              </div>

              <div data-id="element-563">
                <label className="block text-sm text-gray-400 mb-2" data-id="element-564">
                  Email
                </label>
                <input type="text" value={formData.email} onChange={e => {
              setFormData({
                ...formData,
                email: e.target.value
              });
              if (formErrors.email) {
                setFormErrors({
                  ...formErrors,
                  email: ''
                });
              }
            }} className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-gray-500 focus:outline-none transition-colors text-base ${formErrors.email ? 'border-red-500' : 'border-white/20 focus:border-blue-500'}`} placeholder="email@example.com" disabled={isSubmitting} data-id="element-565" />
                {formErrors.email && <p className="text-red-400 text-sm mt-1" data-id="element-566">
                    {formErrors.email}
                  </p>}
              </div>

              <div data-id="element-567">
                <label className="block text-sm text-gray-400 mb-2" data-id="element-568">
                  Назва компанії
                </label>
                <input type="text" value={formData.company} onChange={e => setFormData({
              ...formData,
              company: e.target.value
            })} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors text-base" placeholder="Назва вашої компанії" disabled={isSubmitting} data-id="element-569" />
              </div>

              {submitStatus === 'error' && <p className="text-red-400 text-sm text-center" data-id="element-570">
                  Виникла помилка. Спробуйте ще раз.
                </p>}

              <Button type="submit" variant="primary" fullWidth className="mt-6" disabled={isSubmitting} data-id="element-571">
                {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-572">
                    <Loader2 className="w-5 h-5 animate-spin" data-id="element-573" />
                    Надсилання...
                  </span> : 'Надіслати заявку'}
              </Button>
            </form>}
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
export function Tariffs() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const openModal = () => {
    setScrollPosition(window.scrollY);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
  };
  // Full content for LEADER tier (all included)
  const leaderSections: TariffSection[] = [{
    title: 'Сесії',
    items: [{
      text: 'Ментальні моделі підприємця',
      included: true
    }, {
      text: 'Unit-економіка для управлінських рішень',
      included: true
    }, {
      text: 'Formula Growth Acquisition',
      included: true
    }, {
      text: 'Product',
      included: true
    }, {
      text: 'Price',
      included: true
    }, {
      text: 'Proposition / Offer',
      included: true
    }, {
      text: 'Promotion',
      included: true
    }, {
      text: 'Place',
      included: true
    }, {
      text: 'People',
      included: true
    }, {
      text: 'Process',
      included: true
    }, {
      text: 'Physical Evidence',
      included: true
    }, {
      text: 'Перехід до Retention',
      included: true
    }]
  }, {
    title: 'Персональна робота',
    items: [{
      text: 'Особиста стратегічна сесія з Нікітою Шпірт (60 хв)',
      included: true
    }, {
      text: 'Персональний аудит маркетингової системи',
      included: true
    }, {
      text: 'Розбір Growth Acquisition + Retention',
      included: true
    }, {
      text: 'Підтримка впровадження',
      included: true
    }]
  }, {
    title: 'Практика',
    items: [{
      text: 'Робота з власним бізнесом або кейсом',
      included: true
    }, {
      text: 'Заповнення системних таблиць по ходу навчання',
      included: true
    }, {
      text: 'Формування цілісної логіки зростання',
      included: true
    }, {
      text: 'Робота з гіпотезами залучення та масштабування',
      included: true
    }, {
      text: 'Корекція Acquisition System',
      included: true
    }, {
      text: 'Корекція Unit-економіки',
      included: true
    }, {
      text: 'Розбір управлінських рішень у процесі',
      included: true
    }, {
      text: 'Створення та захист індивідуальної стратегії Acquisition',
      included: true
    }]
  }, {
    title: 'Артефакти',
    items: [{
      text: 'Портрет бізнесу (документ)',
      included: true
    }, {
      text: 'Acquisition System (таблиця 8 макрорівнів, 300 мікроелементів)',
      included: true
    }, {
      text: 'Карта гіпотез Growth Hacking (таблиця)',
      included: true
    }, {
      text: 'Unit-економіка (розрахункова модель)',
      included: true
    }, {
      text: 'Dashboard / аналітична панель',
      included: true
    }, {
      text: 'Чек-лист ментальних моделей',
      included: true
    }]
  }, {
    title: 'Підтримка',
    items: [{
      text: 'Доступ до всіх матеріалів курсу',
      included: true
    }, {
      text: 'Записи всіх сесій',
      included: true
    }, {
      text: 'Активна участь у чаті',
      included: true
    }, {
      text: 'Зворотний звʼязок по артефактах',
      included: true
    }, {
      text: 'Рекомендації щодо впровадження рішень',
      included: true
    }, {
      text: 'Пріоритетні відповіді від команди',
      included: true
    }, {
      text: 'Прямий доступ до команди ARMAR',
      included: true
    }, {
      text: 'Перевірка рішень та гіпотез',
      included: true
    }, {
      text: 'Персональний зворотний звʼязок',
      included: true
    }, {
      text: 'Запис персональних сесій',
      included: true
    }, {
      text: 'Довгостроковий доступ до чату',
      included: true
    }]
  }, {
    title: 'Комʼюніті',
    items: [{
      text: 'Новинний канал програми',
      included: true
    }, {
      text: 'Закритий чат учасників',
      included: true
    }]
  }, {
    title: 'Додатково в тарифі',
    items: [{
      text: 'Доступ до 3 консультацій від топів ARMAR',
      included: true
    }]
  }];
  // DECISION MAKER sections
  const decisionMakerSections: TariffSection[] = [{
    title: 'Сесії',
    items: [{
      text: 'Ментальні моделі підприємця',
      included: true
    }, {
      text: 'Unit-економіка для управлінських рішень',
      included: true
    }, {
      text: 'Formula Growth Acquisition',
      included: true
    }, {
      text: 'Product',
      included: true
    }, {
      text: 'Price',
      included: true
    }, {
      text: 'Proposition / Offer',
      included: true
    }, {
      text: 'Promotion',
      included: true
    }, {
      text: 'Place',
      included: true
    }, {
      text: 'People',
      included: true
    }, {
      text: 'Process',
      included: true
    }, {
      text: 'Physical Evidence',
      included: true
    }, {
      text: 'Перехід до Retention',
      included: true
    }]
  }, {
    title: 'Персональна робота',
    items: [{
      text: 'Особиста стратегічна сесія з Нікітою Шпірт (60 хв)',
      included: false
    }, {
      text: 'Персональний аудит маркетингової системи',
      included: false
    }, {
      text: 'Розбір Growth Acquisition + Retention',
      included: false
    }, {
      text: 'Підтримка впровадження',
      included: false
    }]
  }, {
    title: 'Практика',
    items: [{
      text: 'Робота з власним бізнесом або кейсом',
      included: true
    }, {
      text: 'Заповнення системних таблиць по ходу навчання',
      included: true
    }, {
      text: 'Формування цілісної логіки зростання',
      included: true
    }, {
      text: 'Робота з гіпотезами залучення та масштабування',
      included: true
    }, {
      text: 'Корекція Acquisition System',
      included: true
    }, {
      text: 'Корекція Unit-економіки',
      included: true
    }, {
      text: 'Розбір управлінських рішень у процесі',
      included: true
    }, {
      text: 'Створення та захист індивідуальної стратегії Acquisition',
      included: false
    }]
  }, {
    title: 'Артефакти',
    items: [{
      text: 'Портрет бізнесу (документ)',
      included: true
    }, {
      text: 'Acquisition System (таблиця 8 макрорівнів, 300 мікроелементів)',
      included: true
    }, {
      text: 'Карта гіпотез Growth Hacking (таблиця)',
      included: true
    }, {
      text: 'Unit-економіка (розрахункова модель)',
      included: true
    }, {
      text: 'Dashboard / аналітична панель',
      included: true
    }, {
      text: 'Чек-лист ментальних моделей',
      included: true
    }]
  }, {
    title: 'Підтримка',
    items: [{
      text: 'Доступ до всіх матеріалів курсу',
      included: true
    }, {
      text: 'Записи всіх сесій',
      included: true
    }, {
      text: 'Активна участь у чаті',
      included: true
    }, {
      text: 'Зворотний звʼязок по артефактах',
      included: true
    }, {
      text: 'Рекомендації щодо впровадження рішень',
      included: true
    }, {
      text: 'Пріоритетні відповіді від команди',
      included: true
    }, {
      text: 'Прямий доступ до команди ARMAR',
      included: false
    }, {
      text: 'Перевірка рішень та гіпотез',
      included: false
    }, {
      text: 'Персональний зворотний звʼязок',
      included: false
    }, {
      text: 'Запис персональних сесій',
      included: false
    }, {
      text: 'Довгостроковий доступ до чату',
      included: false
    }]
  }, {
    title: 'Комʼюніті',
    items: [{
      text: 'Новинний канал програми',
      included: true
    }, {
      text: 'Закритий чат учасників',
      included: true
    }]
  }, {
    title: 'Додатково в тарифі',
    items: [{
      text: 'Доступ до 3 консультацій від топів ARMAR',
      included: false
    }]
  }];
  // OBSERVER sections
  const observerSections: TariffSection[] = [{
    title: 'Сесії',
    items: [{
      text: 'Ментальні моделі підприємця',
      included: true
    }, {
      text: 'Unit-економіка для управлінських рішень',
      included: true
    }, {
      text: 'Formula Growth Acquisition',
      included: true
    }, {
      text: 'Product',
      included: true
    }, {
      text: 'Price',
      included: true
    }, {
      text: 'Proposition / Offer',
      included: true
    }, {
      text: 'Promotion',
      included: true
    }, {
      text: 'Place',
      included: true
    }, {
      text: 'People',
      included: true
    }, {
      text: 'Process',
      included: true
    }, {
      text: 'Physical Evidence',
      included: true
    }, {
      text: 'Перехід до Retention',
      included: true
    }]
  }, {
    title: 'Персональна робота',
    items: [{
      text: 'Особиста стратегічна сесія з Нікітою Шпірт (60 хв)',
      included: false
    }, {
      text: 'Персональний аудит маркетингової системи',
      included: false
    }, {
      text: 'Розбір Growth Acquisition + Retention',
      included: false
    }, {
      text: 'Підтримка впровадження',
      included: false
    }]
  }, {
    title: 'Практика',
    items: [{
      text: 'Робота з власним бізнесом або кейсом',
      included: true
    }, {
      text: 'Заповнення системних таблиць по ходу навчання',
      included: true
    }, {
      text: 'Формування цілісної логіки зростання',
      included: true
    }, {
      text: 'Робота з гіпотезами залучення та масштабування',
      included: false
    }, {
      text: 'Корекція Acquisition System',
      included: false
    }, {
      text: 'Корекція Unit-економіки',
      included: false
    }, {
      text: 'Розбір управлінських рішень у процесі',
      included: false
    }, {
      text: 'Створення та захист індивідуальної стратегії Acquisition',
      included: false
    }]
  }, {
    title: 'Артефакти',
    items: [{
      text: 'Портрет бізнесу (документ)',
      included: true
    }, {
      text: 'Acquisition System (таблиця 8 макрорівнів, 300 мікроелементів)',
      included: true
    }, {
      text: 'Карта гіпотез Growth Hacking (таблиця)',
      included: true
    }, {
      text: 'Unit-економіка (розрахункова модель)',
      included: true
    }, {
      text: 'Dashboard / аналітична панель',
      included: true
    }, {
      text: 'Чек-лист ментальних моделей',
      included: true
    }]
  }, {
    title: 'Підтримка',
    items: [{
      text: 'Доступ до всіх матеріалів курсу',
      included: true
    }, {
      text: 'Записи всіх сесій',
      included: true
    }, {
      text: 'Активна участь у чаті',
      included: false
    }, {
      text: 'Зворотний звʼязок по артефактах',
      included: false
    }, {
      text: 'Рекомендації щодо впровадження рішень',
      included: false
    }, {
      text: 'Пріоритетні відповіді від команди',
      included: false
    }, {
      text: 'Прямий доступ до команди ARMAR',
      included: false
    }, {
      text: 'Перевірка рішень та гіпотез',
      included: false
    }, {
      text: 'Персональний зворотний звʼязок',
      included: false
    }, {
      text: 'Запис персональних сесій',
      included: false
    }, {
      text: 'Довгостроковий доступ до чату',
      included: false
    }]
  }, {
    title: 'Комʼюніті',
    items: [{
      text: 'Новинний канал програми',
      included: true
    }, {
      text: 'Закритий чат учасників',
      included: true
    }]
  }, {
    title: 'Додатково в тарифі',
    items: [{
      text: 'Доступ до 3 консультацій від топів ARMAR',
      included: false
    }]
  }];
  const tiers: Tier[] = [{
    name: 'OBSERVER',
    price: '$590',
    sections: observerSections,
    cta: 'Обрати рівень Observer',
    ctaSecondary: 'Забронювати місце',
    popular: false,
    isLeader: false,
    href: 'https://secure.wayforpay.com/qr/q10e4e52e372c'
  }, {
    name: 'DECISION MAKER',
    price: '$990',
    sections: decisionMakerSections,
    cta: 'Обрати рівень Decision Maker',
    ctaSecondary: 'Забронювати місце',
    popular: true,
    isLeader: false,
    href: 'https://secure.wayforpay.com/qr/q12593e46f2a8'
  }, {
    name: 'LEADER',
    price: '$1490',
    sections: leaderSections,
    cta: 'Обрати рівень Leader',
    ctaSecondary: 'Забронювати місце',
    popular: false,
    isLeader: true,
    href: 'https://secure.wayforpay.com/qr/q84658c605b62'
  }];
  const handleCtaClick = (tier: Tier) => {
    window.open(tier.href, '_blank');
  };
  const getBorderColor = (tier: Tier) => {
    if (tier.popular) return 'border-blue-500 ring-2 ring-blue-500/20';
    if (tier.isLeader) return 'border-purple-300';
    return 'border-gray-200';
  };
  return <>
      <Section id="tariffs" className="bg-transparent py-16 md:py-24" data-id="element-574">
        <style data-id="element-575">{`
          @keyframes gradient-x {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }
          .animate-gradient-x {
            animation: gradient-x 3s ease infinite;
          }
        `}</style>

        <div className="text-center mb-10 md:mb-16 px-4" data-id="element-576">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-3 md:mb-4" data-id="element-577">
            Вартість участі
          </h2>
          <p className="text-sm md:text-base text-gray-400" data-id="element-578">
            Оберіть формат, який підходить саме вам
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-7xl mx-auto items-start px-4" data-id="element-579">
          {tiers.map((tier, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} data-id="element-580">
              <div className={`flex flex-col relative bg-white border rounded-2xl p-5 md:p-6 shadow-lg ${getBorderColor(tier)}`} data-id="element-581">
                {tier.popular && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-blue-600 via-cyan-400 to-blue-600 bg-[length:200%_auto] animate-gradient-x text-white text-xs font-bold px-3 md:px-4 py-1 md:py-1.5 rounded-full uppercase tracking-wider whitespace-nowrap" data-id="element-582">
                    Найпопулярніший
                  </div>}

                {/* Header */}
                <div className="mb-5 md:mb-6 pb-5 md:pb-6 border-b border-gray-200" data-id="element-583">
                  <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-1 tracking-wide" data-id="element-584">
                    {tier.name}
                  </h3>
                  <div className="text-3xl md:text-4xl font-bold mb-2" data-id="element-585">
                    <AnimatedGradientText isLeader={tier.isLeader} data-id="element-586">
                      {tier.price}
                    </AnimatedGradientText>
                  </div>
                </div>

                {/* Sections */}
                <div className="space-y-4 md:space-y-5 mb-6 md:mb-8 flex-1" data-id="element-587">
                  {tier.sections.map((section, sectionIndex) => <div key={sectionIndex} data-id="element-588">
                      <h4 className="font-semibold text-xs md:text-sm uppercase tracking-wider mb-2 md:mb-3" data-id="element-589">
                        <AnimatedGradientText isLeader={tier.isLeader} data-id="element-590">
                          {section.title}
                        </AnimatedGradientText>
                      </h4>
                      <ul className="space-y-1.5 md:space-y-2" data-id="element-591">
                        {section.items.map((item, itemIndex) => <li key={itemIndex} className="flex items-start gap-2" data-id="element-592">
                            {item.included ? <span className={`shrink-0 mt-0.5 ${tier.isLeader ? 'text-purple-600' : 'text-blue-600'}`} data-id="element-593">
                                <Check className="w-4 h-4" data-id="element-594" />
                              </span> : <X className="w-4 h-4 text-gray-300 shrink-0 mt-0.5" data-id="element-595" />}
                            <span className={`text-xs md:text-sm leading-relaxed ${item.included ? 'text-slate-700' : 'text-gray-400 line-through'}`} data-id="element-596">
                              {item.text}
                            </span>
                          </li>)}
                      </ul>
                    </div>)}
                </div>

                {/* CTA */}
                <div className="space-y-2 md:space-y-3" data-id="element-597">
                  <Button variant="primary" fullWidth className={tier.isLeader ? '!bg-gradient-to-r !from-purple-600 !via-purple-500 !to-purple-400' : ''} onClick={() => handleCtaClick(tier)} data-id="element-598">
                    {tier.cta}
                  </Button>
                  {tier.ctaSecondary && <p className="text-center text-gray-400 text-xs" data-id="element-599">
                      {tier.ctaSecondary}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Special Offers Cards */}
        <div className="flex flex-col gap-4 md:gap-5 max-w-5xl mx-auto mt-10 md:mt-16 px-4" data-id="element-600">
          {/* Veterans Card - Green/Olive theme with pattern */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="relative overflow-hidden rounded-2xl" data-id="element-601">
            {/* Solid green background */}
            <div className="absolute inset-0 bg-[#2a3f2a]" data-id="element-602" />

            {/* Cross pattern overlay */}
            <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234a5f4a' fill-opacity='0.8'%3E%3Cpath d='M9 0h2v20H9zM0 9h20v2H0z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '24px 24px'
          }} data-id="element-603" />

            <div className="relative z-10 p-5 md:p-6" data-id="element-604">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-605">
                <div className="flex items-center gap-4 flex-1" data-id="element-606">
                  <div className="p-3 rounded-xl bg-white/10 shrink-0" data-id="element-607">
                    <Shield className="w-6 h-6 text-white" data-id="element-608" />
                  </div>
                  <div data-id="element-609">
                    <h3 className="text-lg md:text-xl font-bold text-white mb-1" data-id="element-610">
                      Для ЗСУ та ветеранів
                    </h3>
                    <p className="text-white/70 text-sm md:text-base leading-relaxed" data-id="element-611">
                      Спеціальні умови для військовослужбовців, ветеранів та їх
                      родин
                    </p>
                  </div>
                </div>
                <button onClick={openModal} className="px-6 py-2.5 rounded-full border-2 border-white/40 text-white font-medium text-sm hover:bg-white/10 transition-colors shrink-0" data-id="element-612">
                  Дізнатися умови
                </button>
              </div>
            </div>
          </motion.div>

          {/* Marketers & Agencies Card - Purple gradient with pattern */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="relative overflow-hidden rounded-2xl" data-id="element-613">
            {/* Purple gradient background */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a4a] via-[#2d2d6d] to-[#4a2d6d]" data-id="element-614" />

            {/* Cross pattern overlay */}
            <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%236b5b95' fill-opacity='0.8'%3E%3Cpath d='M9 0h2v20H9zM0 9h20v2H0z'/%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '24px 24px'
          }} data-id="element-615" />

            <div className="relative z-10 p-5 md:p-6" data-id="element-616">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-617">
                <div className="flex items-center gap-4 flex-1" data-id="element-618">
                  <div className="p-3 rounded-xl bg-purple-500/20 shrink-0" data-id="element-619">
                    <Users2 className="w-6 h-6 text-purple-300" data-id="element-620" />
                  </div>
                  <div data-id="element-621">
                    <h3 className="text-lg md:text-xl font-bold text-white mb-1" data-id="element-622">
                      Для маркетологів та агенцій
                    </h3>
                    <p className="text-white/70 text-sm md:text-base leading-relaxed" data-id="element-623">
                      Спеціальна програма для маркетинг-спеціалістів та агентств
                      з особливими умовами
                    </p>
                  </div>
                </div>
                <button onClick={openModal} className="px-6 py-2.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium text-sm hover:from-purple-600 hover:to-pink-600 transition-all shrink-0" data-id="element-624">
                  Дізнатися умови
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      <ConsultationModal isOpen={isModalOpen} onClose={closeModal} scrollPosition={scrollPosition} data-id="element-625" />
    </>;
}